/**
 * Template Instantiation Service
 * Converts template structures from unified template library into actual Component objects
 * with unique IDs, maintaining parent-child relationships and semantic metadata
 */

import { Component, ComponentType, Position, ResponsivePosition, Size } from '@/types';
import type { ColorScheme } from '@/types/color-scheme';
import { v4 as uuidv4 } from 'uuid';
import templateLibrary from '@/data/template-library.json';

// ============================================================================
// Type Definitions
// ============================================================================

export interface TemplateMetadataInfo {
  id: string;
  name: string;
  abstraction: 'atom' | 'molecule' | 'organism' | 'page';
  difficulty: 'simple' | 'intermediate' | 'complex';
  componentCount: number;
  hierarchyDepth: number;
  domain: string[];
  use_cases: string[];
  contains: string[];
  componentTypes: string[];
  tags: string[];
  description: string;
  file: string;
  schemaVersion?: 1 | 2;
}

export interface TemplateLibrary {
  version: string;
  description: string;
  metadata: {
    totalTemplates: number;
    categories: string[];
    domains: string[];
  };
  templates: {
    atoms: TemplateMetadataInfo[];
    molecules: TemplateMetadataInfo[];
    organisms: TemplateMetadataInfo[];
    pages: TemplateMetadataInfo[];
  };
  statistics?: unknown;
}

export interface TemplateSearchResult {
  metadata: TemplateMetadataInfo;
  structure?: TemplateFile; // Loaded lazily
}

export interface TemplateSearchFilters {
  abstraction?: 'atom' | 'molecule' | 'organism' | 'page';
  domain?: string;
  difficulty?: 'simple' | 'intermediate' | 'complex';
  contains?: string; // Component type filter
}

export interface TemplateInstance {
  components: Component[];
  componentMap: Map<string, Component>; // templateId -> component
  semanticMap: Map<string, string>; // semantic role -> componentId
  rootComponentId: string;
  templateId: string;
  templateName: string;
  schemaVersion: 1 | 2;
}

export interface TemplateMetadata {
  templateId: string;
  componentId: string;
  semanticRole?: string;
  parentTemplateId?: string;
}

type JsonRecord = Record<string, unknown>;
type TemplateFile = JsonRecord;
type TemplateNode = JsonRecord;

const COMPONENT_TYPE_VALUES = new Set<string>(Object.values(ComponentType));
const TEMPLATE_ABSTRACTIONS = new Set<string>(['atom', 'molecule', 'organism', 'page']);
const TEMPLATE_DIFFICULTIES = new Set<string>(['simple', 'intermediate', 'complex']);

// ============================================================================
// Template Instantiation Service
// ============================================================================

export class TemplateInstantiationService {
  private static instance: TemplateInstantiationService;
  private templateMetadata: Map<string, TemplateMetadata> = new Map();
  private activeTemplates: Map<string, TemplateInstance> = new Map();
  private templateCache: Map<string, TemplateFile> = new Map(); // Cache loaded template files
  private library: TemplateLibrary;

  private constructor() {
    this.library = this.parseTemplateLibrary(templateLibrary);
  }

  private normalizeSemanticKey(value: string): string {
    return value.trim().toLowerCase().replace(/\s+/g, '-');
  }

  private getSemanticReferenceCandidates(node: TemplateNode): string[] {
    const properties = this.getRecord(node.properties);
    const candidates = [
      node.semanticRole,
      node.name,
      properties?.title,
      node.id,
      node.type,
    ];

    const uniqueCandidates = new Set<string>();

    for (const candidate of candidates) {
      if (typeof candidate !== 'string') {
        continue;
      }

      const normalizedCandidate = this.normalizeSemanticKey(candidate);
      if (normalizedCandidate.length > 0) {
        uniqueCandidates.add(normalizedCandidate);
      }
    }

    return Array.from(uniqueCandidates);
  }

  static getInstance(): TemplateInstantiationService {
    if (!TemplateInstantiationService.instance) {
      TemplateInstantiationService.instance = new TemplateInstantiationService();
    }
    return TemplateInstantiationService.instance;
  }

  // ============================================================================
  // NEW: Hierarchical Search Method
  // ============================================================================

  /**
   * Find templates by hierarchy level (abstraction)
   * @param level - The abstraction level to search ('atom', 'molecule', 'organism', 'page', or 'template')
   * @returns Array of template metadata matching the hierarchy level
   */
  findTemplateByHierarchy(
    level: 'atom' | 'molecule' | 'organism' | 'template' | 'page'
  ): TemplateMetadataInfo[] {
    // Handle 'template' as alias for 'page'
    const normalizedLevel = level === 'template' ? 'page' : level;
    const pluralLevel = this.getTemplateGroupKey(normalizedLevel);

    const results = this.library.templates[pluralLevel] || [];

    return results;
  }

  // ============================================================================
  // ENHANCED: Template Search with Filtering
  // ============================================================================

  /**
   * Find a template by name or ID with enhanced search and optional filtering
   * @param nameOrId - Template name, ID, tag, use case, or domain to search for
   * @param filters - Optional filters for abstraction, domain, difficulty, contains
   * @returns Template search result with metadata, or null if not found
   */
  findTemplate(
    nameOrId: string,
    filters?: TemplateSearchFilters
  ): TemplateSearchResult | null {
    const normalizedSearch = nameOrId.toLowerCase().trim();

    // Search across all hierarchy levels
    const allTemplates: TemplateMetadataInfo[] = [
      ...this.library.templates.atoms,
      ...this.library.templates.molecules,
      ...this.library.templates.organisms,
      ...this.library.templates.pages
    ];

    // Apply filters if provided
    let filteredTemplates = allTemplates;

    if (filters?.abstraction) {
      filteredTemplates = filteredTemplates.filter(
        t => t.abstraction === filters.abstraction
      );
    }

    if (filters?.domain) {
      const domainFilter = filters.domain.toLowerCase();
      filteredTemplates = filteredTemplates.filter(
        t => t.domain.some(d => d.toLowerCase().includes(domainFilter))
      );
    }

    if (filters?.difficulty) {
      filteredTemplates = filteredTemplates.filter(
        t => t.difficulty === filters.difficulty
      );
    }

    if (filters?.contains) {
      const containsFilter = filters.contains.toLowerCase();
      filteredTemplates = filteredTemplates.filter(
        t => t.contains.some(c => c.toLowerCase().includes(containsFilter)) ||
            t.componentTypes.some(ct => ct.toLowerCase().includes(containsFilter))
      );
    }

    // Search for matches in ID, name, tags, use_cases, domain, contains
    const matchedTemplate = filteredTemplates.find(template => {
      // Exact ID match
      if (template.id === nameOrId) {
        return true;
      }

      // Name match (partial)
      if (template.name.toLowerCase().includes(normalizedSearch)) {
        return true;
      }

      // Tags match
      if (template.tags.some(tag => tag.toLowerCase().includes(normalizedSearch))) {
        return true;
      }

      // Use cases match
      if (template.use_cases.some(uc => uc.toLowerCase().includes(normalizedSearch))) {
        return true;
      }

      // Domain match
      if (template.domain.some(d => d.toLowerCase().includes(normalizedSearch))) {
        return true;
      }

      // Contains match (component types)
      if (template.contains.some(c => c.toLowerCase().includes(normalizedSearch))) {
        return true;
      }

      // Component types match
      if (template.componentTypes.some(ct => ct.toLowerCase().includes(normalizedSearch))) {
        return true;
      }

      return false;
    });

    if (!matchedTemplate) {
      return null;
    }

    return {
      metadata: matchedTemplate
    };
  }

  // ============================================================================
  // NEW: Load Full Template from File Path
  // ============================================================================

  /**
   * Load full template structure from file path (with caching)
   * @param filePath - Relative path to template file (e.g., "templates/pages/sensor-dashboard.json")
   * @returns Promise resolving to template structure, or null if failed
   */
  private async loadTemplateFile(filePath: string): Promise<TemplateFile | null> {
    // Check cache first
    if (this.templateCache.has(filePath)) {
      return this.templateCache.get(filePath) ?? null;
    }


    try {
      const response = await fetch(`/data/${filePath}`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const template: unknown = await response.json();
      if (!this.isRecord(template)) {
        throw new Error('Template file root must be an object');
      }

      // Cache the loaded template
      this.templateCache.set(filePath, template);

      return template;
    } catch (error) {
      console.error('❌ [TEMPLATE LOAD] Failed to load template:', {
        filePath,
        error: error instanceof Error ? error.message : String(error)
      });
      return null;
    }
  }

  // ============================================================================
  // NEW: Convert Page Components Array to Nested Structure
  // ============================================================================

  /**
   * Convert flat components array (with children IDs) to nested structure
   * @param components - Flat array of components from template file
   * @returns Root component with nested children structure
   */
  private convertPageComponentsToStructure(components: unknown[]): TemplateNode | null {
    if (!components || components.length === 0) {
      return null;
    }

    // Create a map for quick lookup
    const componentMap = new Map<string, TemplateNode>();
    components.forEach(comp => {
      if (!this.isRecord(comp)) return;
      const id = this.getString(comp.id);
      if (!id) return;
      componentMap.set(id, { ...comp });
    });

    // Build nested structure
    components.forEach(comp => {
      if (!this.isRecord(comp)) return;
      const compId = this.getString(comp.id);
      if (!compId) return;
      const children = Array.isArray(comp.children) ? comp.children : [];
      if (children.length > 0) {
        const nestedChildren: TemplateNode[] = [];

        children.forEach(childId => {
          if (typeof childId !== 'string') return;
          const childComp = componentMap.get(childId);
          if (childComp) {
            nestedChildren.push(childComp);
          }
        });

        const parent = componentMap.get(compId);
        if (parent) {
          parent.children = nestedChildren;
        }
      }
    });

    // Find root component (no parentId or first component)
    const rootComponent = components.find(comp => this.isRecord(comp) && !comp.parentId) || components[0];

    if (!this.isRecord(rootComponent)) {
      return null;
    }

    const rootId = this.getString(rootComponent.id);
    if (!rootId) {
      return null;
    }

    const root = componentMap.get(rootId);

    return root ?? null;
  }

  // ============================================================================
  // ENHANCED: Instantiate Template with Lazy Loading
  // ============================================================================

  /**
   * Instantiate a template into actual components
   */
  async instantiateTemplate(
    templateNameOrId: string,
    projectId: string,
    pageId: string,
    position?: { x: number; y: number },
    size?: { width: number; height: number },
    activeColorScheme?: ColorScheme
  ): Promise<TemplateInstance | null> {
    // Step 1: Find template metadata
    const searchResult = this.findTemplate(templateNameOrId);
    if (!searchResult) {
      console.warn(`❌ [TEMPLATE] Template not found: ${templateNameOrId}`);
      return null;
    }

    const metadata = searchResult.metadata;

    // Step 2: Load full template from file
    const templateFile = await this.loadTemplateFile(metadata.file);
    if (!templateFile) {
      console.error('❌ [TEMPLATE] Failed to load template file:', metadata.file);
      return null;
    }

    // Step 3: Extract components from template file structure
    let templateStructure: TemplateNode | null = null;

    if (Array.isArray(templateFile.pages) && templateFile.pages.length > 0) {
      // New format: extract from pages[0].components
      const firstPage = templateFile.pages[0];
      const componentsArray = this.isRecord(firstPage) ? firstPage.components : undefined;
      if (Array.isArray(componentsArray)) {
        templateStructure = this.convertPageComponentsToStructure(componentsArray);
      }
    } else if (this.isRecord(templateFile.structure)) {
      // Legacy format: direct structure property
      templateStructure = templateFile.structure;
    }

    if (!templateStructure) {
      console.error('❌ [TEMPLATE] Invalid template structure:', {
        hasPages: !!templateFile.pages,
        hasStructure: !!templateFile.structure
      });
      return null;
    }

    // Step 4: Convert to component instances
    const instanceId = uuidv4();
    const components: Component[] = [];
    const componentMap = new Map<string, Component>();
    const semanticMap = new Map<string, string>();

    const rootComponent = this.convertTemplateNodeToComponent(
      templateStructure,
      projectId,
      pageId,
      position || { x: 100, y: 100 },
      undefined,
      components,
      componentMap,
      semanticMap,
      instanceId,
      true, // isRoot flag
      size, // pass size for root component
      activeColorScheme
    );

    if (!rootComponent) {
      console.error('❌ [TEMPLATE] Failed to create root component from template');
      return null;
    }

    // Remap cross-component property references (e.g. linkedChartId) from
    // template node IDs to the new UUIDs assigned during instantiation.
    components.forEach(component => {
      const linkedChartId = component.properties?.linkedChartId;
      if (typeof linkedChartId === 'string') {
        const oldId = linkedChartId;
        const newComp = componentMap.get(oldId);
        if (newComp) {
          component.properties = {
            ...component.properties,
            linkedChartId: newComp.id,
          };
        }
      }
    });

    const instance: TemplateInstance = {
      components,
      componentMap,
      semanticMap,
      rootComponentId: rootComponent.id,
      templateId: metadata.id,
      templateName: metadata.name,
      schemaVersion: this.detectTemplateSchemaVersion(templateFile),
    };

    // Store the instance for future reference
    this.activeTemplates.set(instanceId, instance);

    return instance;
  }

  private detectTemplateSchemaVersion(templateFile: unknown): 1 | 2 {
    if (!this.isRecord(templateFile)) return 1;

    const components = this.getTemplateComponentsForSchemaDetection(templateFile);
    if (components.length === 0) return templateFile.schemaVersion === 2 ? 2 : 1;

    return components.every(component => this.componentHasV2Shape(component)) ? 2 : 1;
  }

  private getTemplateComponentsForSchemaDetection(templateFile: Record<string, unknown>): Record<string, unknown>[] {
    if (Array.isArray(templateFile.pages)) {
      return templateFile.pages.flatMap(page => {
        if (!this.isRecord(page) || !Array.isArray(page.components)) return [];
        return page.components.filter((component): component is Record<string, unknown> => this.isRecord(component));
      });
    }

    if (this.isRecord(templateFile.structure)) {
      const components: Record<string, unknown>[] = [];
      const visit = (component: Record<string, unknown>): void => {
        components.push(component);
        if (!Array.isArray(component.children)) return;
        for (const child of component.children) {
          if (this.isRecord(child)) visit(child);
        }
      };
      visit(templateFile.structure);
      return components;
    }

    if (this.isRecord(templateFile.component)) {
      const components: Record<string, unknown>[] = [];
      const visit = (component: Record<string, unknown>): void => {
        components.push(component);
        if (!Array.isArray(component.children)) return;
        for (const child of component.children) {
          if (this.isRecord(child)) visit(child);
        }
      };
      visit(templateFile.component);
      return components;
    }

    return [];
  }

  private componentHasV2Shape(component: Record<string, unknown>): boolean {
    const style = component.style;
    if (!this.isRecord(style) || !this.isRecord(style.base)) return false;

    const responsivePosition = component.responsivePosition;
    if (this.isRecord(responsivePosition) && this.isRecord(responsivePosition.breakpoints)) {
      return false;
    }

    return true;
  }

  // ============================================================================
  // Convert Template Node to Component (Recursive)
  // ============================================================================

  /**
   * Convert a template node to a Component (recursive for children)
   */
  private convertTemplateNodeToComponent(
    node: TemplateNode,
    projectId: string,
    pageId: string,
    position: { x: number; y: number },
    parentId: string | undefined,
    componentsArray: Component[],
    componentMap: Map<string, Component>,
    semanticMap: Map<string, string>,
    templateInstanceId: string,
    isRoot: boolean = false,
    rootSize?: { width: number; height: number },
    activeColorScheme?: ColorScheme
  ): Component | null {
    const componentId = uuidv4();

    // Determine component type - default to FLEX_CONTAINER for layout consistency
    const nodeType = this.getString(node.type);
    const nodeProperties = this.getRecord(node.properties) ?? {};
    const nodeId = this.getString(node.id);
    const nodeName = this.getString(node.name);
    const nodeSemanticRole = this.getString(node.semanticRole);
    const nodeTitle = this.getString(nodeProperties.title);
    const nodeLabel = nodeSemanticRole || nodeName || nodeTitle || nodeId || nodeType || 'component';

    let componentType: ComponentType = ComponentType.FLEX_CONTAINER;
    if (nodeType === 'flex-container' || nodeType === 'container') {
      componentType = ComponentType.FLEX_CONTAINER;
    } else if (nodeType === 'text') {
      componentType = ComponentType.TEXT;
    } else if (nodeType === 'button') {
      componentType = ComponentType.BUTTON;
    } else if (nodeType === 'input') {
      componentType = ComponentType.INPUT;
    } else if (nodeType === 'image') {
      componentType = ComponentType.IMAGE;
    } else if (this.isComponentType(nodeType)) {
      componentType = nodeType;
    }

    const nodeStyle = this.getRecord(node.style) ?? {};

    // Determine size from the structural size field only. v2 templates keep CSS
    // dimensions in style.base/breakpoint slots, and copying them back into
    // component.size reintroduces the legacy shape that migration removed.
    let componentSize = this.getSize(node.size) ?? { width: 200, height: 100 };
    if (isRoot && rootSize) {
      componentSize = rootSize;
    }
    const nodePosition = this.getPosition(node.position);
    const nodeResponsivePosition = this.getResponsivePosition(node.responsivePosition);

    // Create the component
    const component: Component = {
      id: componentId,
      projectId,
      pageId,
      parentId,
      type: componentType,
      name: nodeName || nodeTitle || nodeType || 'component',
      properties: {
        ...nodeProperties,
        // Preserve template metadata
        _templateInstanceId: templateInstanceId,
        _templateNodeId: nodeId,
        _semanticRole: nodeLabel,
      },
      position: nodePosition || { ...position, z: 1 },
      size: componentSize,
      style: nodeStyle,
      // Flex-only architecture (December 2025)
      responsivePosition: nodeResponsivePosition ?? {
        strategy: 'flex'
      },
      children: [],
      locked: false,
      hidden: false,
      order: Date.now(),
      version: 1,
      createdBy: 'ai-template',
      lastModifiedBy: 'ai-template',
      lastModifiedAt: new Date(),
      isDeleted: false,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    // Store metadata
    const metadata: TemplateMetadata = {
      templateId: templateInstanceId,
      componentId,
      semanticRole: nodeLabel,
      parentTemplateId: nodeId
    };
    this.templateMetadata.set(componentId, metadata);

    // Map semantic references to component ID using normalized keys.
    for (const candidate of this.getSemanticReferenceCandidates(node)) {
      semanticMap.set(candidate, componentId);
    }

    // Add to component map and array
    componentMap.set(nodeId || componentId, component);
    componentsArray.push(component);

    // Process children recursively
    const children = Array.isArray(node.children)
      ? node.children.filter((child): child is TemplateNode => this.isRecord(child))
      : [];
    if (children.length > 0) {
      const childIds: string[] = [];

      // Calculate scale factor if this is root with custom size
      let scaleFactor = 1;
      const numericNodeSize = this.getNumericSize(node.size);
      if (isRoot && rootSize && numericNodeSize) {
        scaleFactor = Math.min(
          rootSize.width / numericNodeSize.width,
          rootSize.height / numericNodeSize.height
        );
      }

      children.forEach((childNode, index: number) => {
        const childPositionValue = this.getPosition(childNode.position);
        // Scale child positions if root is scaled
        const childPosition = {
          x: position.x + ((childPositionValue?.x || (index * 20)) * scaleFactor),
          y: position.y + ((childPositionValue?.y || (index * 20)) * scaleFactor),
        };

        // Scale child size if defined
        const childSize = this.getNumericSize(childNode.size);
        const nextChildNode = childSize && scaleFactor !== 1
          ? {
            ...childNode,
            size: {
              width: childSize.width * scaleFactor,
              height: childSize.height * scaleFactor
            }
          }
          : childNode;

        const childComponent = this.convertTemplateNodeToComponent(
          nextChildNode,
          projectId,
          pageId,
          childPosition,
          componentId,
          componentsArray,
          componentMap,
          semanticMap,
          templateInstanceId,
          false, // not root
          undefined, // no custom size for children
          activeColorScheme
        );

        if (childComponent) {
          childIds.push(childComponent.id);
        }
      });
      component.children = childIds;
    }

    return component;
  }

  // ============================================================================
  // Existing Methods (Updated to use new library)
  // ============================================================================

  /**
   * Get available template patterns
   */
  getAvailableTemplates(): Array<{
    id: string;
    name: string;
    description: string;
    tags: string[];
    abstraction: string;
    domain: string[];
    componentCount: number;
    hierarchyDepth: number;
  }> {
    const allTemplates: TemplateMetadataInfo[] = [
      ...this.library.templates.atoms,
      ...this.library.templates.molecules,
      ...this.library.templates.organisms,
      ...this.library.templates.pages
    ];

    return allTemplates.map(template => ({
      id: template.id,
      name: template.name,
      description: template.description,
      tags: template.tags,
      abstraction: template.abstraction,
      domain: template.domain,
      componentCount: template.componentCount,
      hierarchyDepth: template.hierarchyDepth
    }));
  }

  /**
   * Match user input to template names (enhanced with new metadata)
   */
  matchTemplatePattern(userInput: string): TemplateSearchResult | null {
    const normalizedInput = userInput.toLowerCase();

    // Search across all templates using enhanced search
    const allTemplates: TemplateMetadataInfo[] = [
      ...this.library.templates.atoms,
      ...this.library.templates.molecules,
      ...this.library.templates.organisms,
      ...this.library.templates.pages
    ];

    // Try exact or partial matches in use_cases first (most specific)
    for (const template of allTemplates) {
      if (template.use_cases.some(uc => normalizedInput.includes(uc.toLowerCase()))) {
        return { metadata: template };
      }
    }

    // Try name matches
    for (const template of allTemplates) {
      if (template.name.toLowerCase().includes(normalizedInput)) {
        return { metadata: template };
      }
    }

    // Try tag matches
    for (const template of allTemplates) {
      if (template.tags.some(tag => normalizedInput.includes(tag.toLowerCase()))) {
        return { metadata: template };
      }
    }

    return null;
  }

  /**
   * Get component by semantic reference within a template
   */
  getComponentBySemanticReference(
    reference: string,
    templateInstanceId?: string
  ): Component | null {
    const normalizedRef = reference.toLowerCase().replace(/\s+/g, '-');

    // If template instance ID is provided, search within that template
    if (templateInstanceId) {
      const instance = this.activeTemplates.get(templateInstanceId);
      if (instance) {
        const componentId = instance.semanticMap.get(normalizedRef);
        if (componentId) {
          return instance.componentMap.get(componentId) || null;
        }
      }
    }

    // Search all active templates
    for (const instance of this.activeTemplates.values()) {
      const componentId = instance.semanticMap.get(normalizedRef);
      if (componentId) {
        return instance.componentMap.get(componentId) || null;
      }
    }

    return null;
  }

  /**
   * Get template metadata for a component
   */
  getComponentMetadata(componentId: string): TemplateMetadata | null {
    return this.templateMetadata.get(componentId) || null;
  }

  /**
   * Check if a component is part of a template
   */
  isTemplateComponent(componentId: string): boolean {
    return this.templateMetadata.has(componentId);
  }

  /**
   * Get all components belonging to a template instance
   */
  getTemplateComponents(templateInstanceId: string): Component[] {
    const instance = this.activeTemplates.get(templateInstanceId);
    return instance ? instance.components : [];
  }

  /**
   * Clear template metadata for cleanup
   */
  clearTemplateInstance(templateInstanceId: string): void {
    const instance = this.activeTemplates.get(templateInstanceId);
    if (instance) {
      // Clear metadata for all components in the instance
      instance.components.forEach(component => {
        this.templateMetadata.delete(component.id);
      });
      this.activeTemplates.delete(templateInstanceId);
    }
  }

  /**
   * Clear template file cache (useful for development/testing)
   */
  clearTemplateCache(): void {
    this.templateCache.clear();
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { size: number; keys: string[] } {
    return {
      size: this.templateCache.size,
      keys: Array.from(this.templateCache.keys())
    };
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  private parseTemplateLibrary(value: unknown): TemplateLibrary {
    if (!this.isRecord(value) || !this.isRecord(value.metadata) || !this.isRecord(value.templates)) {
      throw new Error('Invalid template library metadata');
    }

    return {
      version: this.getString(value.version) ?? '',
      description: this.getString(value.description) ?? '',
      metadata: {
        totalTemplates: this.getNumber(value.metadata.totalTemplates) ?? 0,
        categories: this.getStringArray(value.metadata.categories),
        domains: this.getStringArray(value.metadata.domains),
      },
      templates: {
        atoms: this.getTemplateMetadataArray(value.templates.atoms),
        molecules: this.getTemplateMetadataArray(value.templates.molecules),
        organisms: this.getTemplateMetadataArray(value.templates.organisms),
        pages: this.getTemplateMetadataArray(value.templates.pages),
      },
      statistics: value.statistics,
    };
  }

  private getTemplateGroupKey(
    level: 'atom' | 'molecule' | 'organism' | 'page',
  ): keyof TemplateLibrary['templates'] {
    switch (level) {
      case 'atom':
        return 'atoms';
      case 'molecule':
        return 'molecules';
      case 'organism':
        return 'organisms';
      case 'page':
        return 'pages';
    }
  }

  private getTemplateMetadataArray(value: unknown): TemplateMetadataInfo[] {
    return Array.isArray(value)
      ? value.filter((item): item is TemplateMetadataInfo => this.isTemplateMetadataInfo(item))
      : [];
  }

  private isTemplateMetadataInfo(value: unknown): value is TemplateMetadataInfo {
    if (!this.isRecord(value)) return false;
    return (
      typeof value.id === 'string' &&
      typeof value.name === 'string' &&
      this.isTemplateAbstraction(value.abstraction) &&
      this.isTemplateDifficulty(value.difficulty) &&
      typeof value.componentCount === 'number' &&
      typeof value.hierarchyDepth === 'number' &&
      this.isStringArray(value.domain) &&
      this.isStringArray(value.use_cases) &&
      this.isStringArray(value.contains) &&
      this.isStringArray(value.componentTypes) &&
      this.isStringArray(value.tags) &&
      typeof value.description === 'string' &&
      typeof value.file === 'string'
    );
  }

  private isTemplateAbstraction(value: unknown): value is TemplateMetadataInfo['abstraction'] {
    return typeof value === 'string' && TEMPLATE_ABSTRACTIONS.has(value);
  }

  private isTemplateDifficulty(value: unknown): value is TemplateMetadataInfo['difficulty'] {
    return typeof value === 'string' && TEMPLATE_DIFFICULTIES.has(value);
  }

  private isStringArray(value: unknown): value is string[] {
    return Array.isArray(value) && value.every(item => typeof item === 'string');
  }

  private getStringArray(value: unknown): string[] {
    return this.isStringArray(value) ? value : [];
  }

  private getString(value: unknown): string | undefined {
    return typeof value === 'string' ? value : undefined;
  }

  private getNumber(value: unknown): number | undefined {
    return typeof value === 'number' ? value : undefined;
  }

  private getRecord(value: unknown): JsonRecord | undefined {
    return this.isRecord(value) ? value : undefined;
  }

  private isComponentType(value: unknown): value is ComponentType {
    return typeof value === 'string' && COMPONENT_TYPE_VALUES.has(value);
  }

  private getSize(value: unknown): Size | undefined {
    if (!this.isRecord(value)) return undefined;
    const { width, height } = value;
    const widthIsValid = typeof width === 'number' || typeof width === 'string';
    const heightIsValid = typeof height === 'number' || typeof height === 'string';
    return widthIsValid && heightIsValid ? { width, height } : undefined;
  }

  private getNumericSize(value: unknown): { width: number; height: number } | undefined {
    if (!this.isRecord(value)) return undefined;
    const { width, height } = value;
    return typeof width === 'number' && typeof height === 'number' ? { width, height } : undefined;
  }

  private getPosition(value: unknown): Position | undefined {
    if (!this.isRecord(value)) return undefined;
    const { x, y, z } = value;
    if (typeof x !== 'number' || typeof y !== 'number') return undefined;
    return { x, y, z: typeof z === 'number' ? z : 1 };
  }

  private getResponsivePosition(value: unknown): ResponsivePosition | undefined {
    return this.isResponsivePosition(value) ? value : undefined;
  }

  private isResponsivePosition(value: unknown): value is ResponsivePosition {
    if (!this.isRecord(value)) return false;
    const strategy = value.strategy;
    return (
      strategy === 'absolute' ||
      strategy === 'relative' ||
      strategy === 'grid' ||
      strategy === 'flex' ||
      strategy === 'auto'
    );
  }
}

export const templateInstantiationService = TemplateInstantiationService.getInstance();
